import { useEffect } from "react";
import type { BrandReport } from "@/pages/home/types";
import { categoryBadgeClass, riskBadgeClass } from "@/pages/home/utils";

interface BrandModalProps {
  report: BrandReport | null;
  onClose: () => void;
}

export default function BrandModal({ report, onClose }: BrandModalProps) {
  useEffect(() => {
    if (!report) return undefined;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [report, onClose]);

  if (!report) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-8">
      <button
        type="button"
        aria-label="关闭详情"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/60 backdrop-blur-sm"
      ></button>

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${report.brandName} 避雷档案详情`}
        className="relative z-10 max-h-full w-full max-w-2xl overflow-y-auto rounded-lg border border-background-300 bg-background-50"
      >
        <div className="flex items-start justify-between gap-4 border-b border-background-200 bg-background-100 p-5 md:p-6">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded px-2 py-0.5 font-label text-xs ${categoryBadgeClass(report.category)}`}>
                {report.categoryLabel}
              </span>
              <span className={`rounded-full border px-2.5 py-0.5 font-label text-xs ${riskBadgeClass(report.riskLevel)}`}>
                风险等级 {report.riskLevel}
              </span>
              <span className="font-label text-xs text-foreground-500">{report.industry}</span>
            </div>
            <h3 className="mt-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {report.brandName} · 避雷档案
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-200"
          >
            <i className="ri-close-line text-lg leading-none"></i>
          </button>
        </div>

        <div className="p-5 md:p-6">
          <p className="text-sm leading-relaxed text-foreground-700">{report.summary}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {report.riskTags.map((tag) => (
              <span key={tag} className="rounded-full border border-background-300 bg-background-100 px-3 py-1 font-label text-xs text-foreground-700">
                # {tag}
              </span>
            ))}
          </div>

          <h4 className="mt-7 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
            <i className="ri-record-circle-line text-base leading-none text-primary-600"></i>
            投稿要点整理
          </h4>
          <ul className="mt-3 flex flex-col gap-3">
            {report.detail.map((line) => (
              <li key={line} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-400"></span>
                {line}
              </li>
            ))}
          </ul>

          <div className="mt-7 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
            <h4 className="flex items-center gap-2 font-heading text-sm font-bold text-secondary-800">
              <i className="ri-lightbulb-flash-line text-base leading-none"></i>
              避坑建议
            </h4>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">{report.advice}</p>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4 font-label text-xs text-foreground-500">
            <span>累计记录 {report.reportCount} 条 · 最近更新 {report.updatedAt}</span>
            <span className="text-foreground-400">条目为投稿整理，品牌名为化名示例</span>
          </div>
        </div>
      </div>
    </div>
  );
}